import { useState, type FormEvent } from "react";
import Reveal from "@/components/feature/Reveal";

const FORM_ENDPOINT = "https://readdy.ai/api/form/daihd7bp14a1h3mr749g";

type Status = "idle" | "loading" | "success" | "error";

export default function SubscribeBand() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [done, setDone] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setDone(true);
      form.reset();
      return;
    }

    payload.delete("contact_alt");
    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const spamLike = `${serverMsg}`.toLowerCase().includes("spam") ||
        `${parsed?.meta?.detail ?? ""}`.toLowerCase().includes("spam");

      if (!response.ok || spamLike || parsed?.code !== "OK") {
        setStatus("error");
        setFormError(serverMsg || "提交没有成功，请稍后再试。");
        return;
      }

      setStatus("success");
      setDone(true);
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络似乎不太稳定，请稍后再试一次。");
    }
  };

  return (
    <section id="subscribe" className="w-full px-4 md:px-10 py-16 md:py-24 bg-background-100">
      <Reveal>
        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-7 p-7 md:p-12">
            <p className="font-label text-xs md:text-sm tracking-[0.24em] text-primary-600">订阅更新</p>
            <h2 className="mt-4 font-heading font-black text-2xl md:text-4xl text-foreground-950 leading-snug">
              有新材料归档时，把整理说明发给你
            </h2>
            <p className="mt-4 max-w-lg font-body text-sm md:text-base leading-7 text-foreground-600">
              只发新增与核对结果，不发一句话结论。留下邮箱，随时可退订。
            </p>

            <form
              id="subscribe-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="relative mt-8 flex flex-col sm:flex-row gap-3"
            >
              <label htmlFor="subscribe-email" className="sr-only">
                邮箱地址
              </label>
              <input
                id="subscribe-email"
                type="email"
                name="email"
                required
                autoComplete="email"
                placeholder="你的邮箱地址"
                className="flex-1 rounded-md border border-background-300 bg-background-50 px-4 py-3 font-body text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
              />

              <label htmlFor="subscribe-focus" className="sr-only">
                关注侧重
              </label>
              <select
                id="subscribe-focus"
                name="focus"
                defaultValue="实锤与对账材料"
                className="rounded-md border border-background-300 bg-background-50 px-4 py-3 font-label text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
              >
                <option value="实锤与对账材料">实锤与对账材料</option>
                <option value="聊天记录与上下文">聊天记录与上下文</option>
                <option value="截图与来源标注">截图与来源标注</option>
                <option value="录音与文字转写">录音与文字转写</option>
              </select>

              <input
                type="text"
                name="contact_alt"
                className="alt-contact-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                defaultValue=""
              />

              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 font-label text-sm text-background-50 dark:text-foreground-950 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                <i
                  className={status === "loading" ? "ri-loader-4-line text-base animate-spin" : "ri-send-plane-line text-base"}
                  aria-hidden="true"
                ></i>
                {status === "loading" ? "提交中" : "订阅更新"}
              </button>
            </form>

            <div className="mt-4 min-h-[24px]" aria-live="polite">
              {status === "success" && done && (
                <p className="flex items-center gap-2 font-label text-sm text-secondary-800">
                  <i className="ri-checkbox-circle-line text-base" aria-hidden="true"></i>
                  已收到，后续有新归档会发到这个邮箱。
                </p>
              )}
              {status === "error" && (
                <p className="flex items-center gap-2 font-label text-sm text-primary-700">
                  <i className="ri-error-warning-line text-base" aria-hidden="true"></i>
                  {formError}
                </p>
              )}
            </div>
          </div>

          <div className="lg:col-span-5 relative min-h-[220px] lg:min-h-full bg-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=quiet%20editorial%20scene%20of%20a%20mail%20tray%20holding%20sealed%20kraft%20envelopes%20beside%20a%20folded%20newspaper%20on%20a%20warm%20cream%20surface%2C%20soft%20daylight%2C%20restrained%20minimal%20still%20life%2C%20no%20people&width=900&height=1100&seq=blackproof-subscribe-01&orientation=portrait"
              alt="订阅更新示意：待寄出的信封与整理好的材料"
              title="黑料爆料更新订阅"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </Reveal>
    </section>
  );
}